import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Modal, Button } from 'react-bootstrap';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { globalErrorHandler } from '../../../utils/GlobalErrorHandler';


const DrugScreeningResult = () => {
    const navigate = useNavigate();
    const debounceTimeout = useRef(null);
    const [guests, setGuests] = useState([]);
    const [guestList, setGuestList] = useState([]);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
    const [filter, setFilter] = useState({
        guest_name: '',
    });
    const [uploadResult, setUploadResult] = useState({
        guest: null,
        year: new Date().getFullYear(),
        month: '',
        week: '',
        files: [],
    });
    const MAX_FILES = 10;
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

    useEffect(() => {
        getAllGuests();
    }, []);

    useEffect(() => {
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            getGuests();
        }, 500);
        return () => clearTimeout(debounceTimeout.current);
    }, [pagination.page, filter]);


    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilter(prev => {
            const updated = { ...prev, [name]: value };

            return updated;
        });
        setPagination(prev => ({ ...prev, page: 1 }));

    };

    const getAllGuests = async () => {
        setIsLoading(true);
        try {
            const res = await axios.post(`${process.env.REACT_APP_WEB_SERVICES_URL}/overseer/get-guest-by-role-by-property`, {}, {
                params: {},
                withCredentials: true
            });

            if (res.data.status) {
                const guests = res.data?.guests || [];
                const guestOptions = guests.map(g => ({
                    value: g.id,
                    label: `${g.name} ${g.email}`
                }));
                setGuestList(guestOptions);
            } else {
                setGuestList([]);
            }
        } catch (error) {
            globalErrorHandler(error, navigate);
        } finally {
            setIsLoading(false);
        }
    };
    const getGuests = async () => {
        setIsLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_WEB_SERVICES_URL}/overseer/get-current-guests`, {
                params: {
                    page: pagination.page,
                    limit: pagination.limit,
                    guest_name: filter.guest_name,
                    property_id: filter.property_id,
                    floor_id: filter.floor_id,
                    room_id: filter.room_id,
                },
                withCredentials: true
            });

            if (res.data.status) {
                setGuests(res.data.guests || []);
                setPagination(res.data.pagination);
            } else {
                setGuests([]);
            }
        } catch (error) {
            globalErrorHandler(error, navigate);
        } finally {
            setIsLoading(false);
        }
    };



    const handleSubmit = async (e) => {
        e.preventDefault();
            
        const { guest, year, month, week, day, files } = uploadResult;

        if (!guest || !year || !month || !week || !day || !files || files.length === 0) {
            Swal.fire('Failed', 'Please fill in all fields and upload at least one file.', 'error');
            return;
        }


        if (files.length > MAX_FILES) {
            Swal.fire('Failed', 'Maximum 10 files allowed.', 'error');
            return;
        }
        const formData = new FormData();
        formData.append('guest_id', guest.value);
        formData.append('year', year);
        formData.append('month', month);
        formData.append('week', week);
        formData.append('day', day);
        files.forEach(file => {
            formData.append('files[]', file);
        });
        try {
            setIsLoading(true);
            const res = await axios.post(
                `${process.env.REACT_APP_WEB_SERVICES_URL}/overseer/upload-drug-screening`,
                formData,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    withCredentials: true,
                }
            );

            if (res.data.status) {
                getGuests(); // or any success refresh logic
                setShowUploadModal(false);
                Swal.fire('Success', 'Result uploaded successfully.', 'success');
            } else {
                Swal.fire('Failed', 'Result cannot be uploaded.', 'error');
            }
        } catch (error) {
            globalErrorHandler(error, navigate);
        } finally {
            setIsLoading(false);
        }
    };





    const clearFilter = () => {
        setFilter({ guest_name: '' });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleChange = (field, value) => {
        setUploadResult(prev => ({ ...prev, [field]: value }));
    };


    return (
        <div className="container-fluid px-4 mt-4">
            <div className='row mb-3' >
                <div className='col'> <h3>Drug Screening Result</h3></div>
                <div className='col text-end' >
                    <button className='btn btn-dark' onClick={() => { setShowUploadModal(true); setUploadResult(null) }} >Upload Result</button>
                </div>
            </div>


            <div className="card shadow mt-4">
                <div className="card-header">
                    <h5 className="mb-0">Search Guests</h5>
                </div>
                <div className="card-body p-4">
                    {/* Filter Section */}
                    <div className="row align-items-end g-2 mb-4">
                        <div className="col-md-3">
                            <div className="position-relative">
                                <input type="text" className="form-control ps-3 pe-5 rounded-3" placeholder="Search Guest" name="guest_name" value={filter.guest_name || ''} onChange={handleFilterChange} />
                                <i className="bi bi-search position-absolute top-50 end-0 translate-middle-y me-3 text-muted" style={{ pointerEvents: 'none' }}   ></i>
                            </div>
                        </div>


                        <div className="col-md-1 d-flex">
                            <button type="button" className="btn btn-dark w-100 rounded-3" onClick={clearFilter}>
                                Clear
                            </button>
                        </div>
                    </div>

                    {/* Guest Table */}

                    <div className="table-responsive">
                        <table className="table table-bordered table-hover shadow-sm nowrap">
                            <thead className="table-success">
                                <tr>
                                    <th>Guest ID</th>
                                    <th>Name</th>
                                    <th>Contact Info</th>
                                    <th>View Result</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading ? (
                                    <tr><td colSpan="11">Loading...</td></tr>
                                ) : guests.length === 0 ? (
                                    <tr><td colSpan="11">No data found.</td></tr>
                                ) : (
                                    guests.map(g => (
                                        <tr key={g.id}>
                                            <td>{`BBM${String(g.id).padStart(4, '0')}`}</td>
                                            <td>{g.name}</td>
                                            <td>{`${g.phone ? g.phone + ' ' : ''}${g.email}`}</td>


                                            <td style={{ 'width': "380px" }}>
                                                <a className="btn btn-success btn-sm me-1" href={`/overseer/drug-screening-result/view-result-files/${g.id}`}>View</a>

                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* Pagination */}
                    <div className="d-flex justify-content-end mt-3">
                        <nav>
                            <ul className="pagination">
                                {[...Array(pagination.totalPages)].map((_, i) => (
                                    <li key={i} className={`page-item ${pagination.page === i + 1 ? 'active' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={() => setPagination(prev => ({ ...prev, page: i + 1 }))}
                                        >
                                            {i + 1}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </div>
            </div>



            <Modal show={showUploadModal} onHide={() => setShowUploadModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Upload Results</Modal.Title>
                </Modal.Header>
                <form onSubmit={handleSubmit} encType="multipart/form-data">
                    <Modal.Body>

                        {/* Guest */}
                        <div className="mb-3">
                            <label className="form-label">Guest</label>
                            <Select
                                options={guestList}
                                value={uploadResult?.guest}
                                onChange={(selected) => handleChange('guest', selected)}
                                placeholder="Search Guest..."
                                isSearchable
                            />
                        </div>

                        {/* Year */}
                        <div className="mb-3">
                            <label className="form-label">Year</label>
                            <select
                                className="form-select"
                                value={uploadResult?.year}
                                onChange={(e) => handleChange('year', e.target.value)}
                            >
                                <option value="">Select Year</option>

                                {Array.from({ length: 20 }, (_, i) => (
                                    <option key={i} value={2025 + i}>{2025 + i}</option>
                                ))}
                            </select>
                        </div>

                        {/* Month */}
                        <div className="mb-3">
                            <label className="form-label">Month</label>
                            <select
                                className="form-select"
                                value={uploadResult?.month}
                                onChange={(e) => handleChange('month', e.target.value)}
                            >
                                <option value="">Select Month</option>
                                {["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((m, i) => (
                                    <option key={i} value={i + 1}>{m}</option>
                                ))}
                            </select>
                        </div>

                        {/* Week */}
                        <div className="mb-3">
                            <label className="form-label">Week</label>
                            <select
                                className="form-select"
                                value={uploadResult?.week}
                                onChange={(e) => handleChange('week', e.target.value)}
                            >
                                <option value="">Select Week</option>
                                {[1, 2, 3, 4, 5].map(w => (
                                    <option key={w} value={w}>Week {w}</option>
                                ))}
                            </select>
                        </div>

{/* Day */}
<div className="mb-3">
  <label className="form-label">Day</label>
  <select
    className="form-select"
    value={uploadResult?.day || ""}
    onChange={(e) => handleChange('day', e.target.value)}
  >
    <option value="">Select Day</option>
    {[
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ].map((d) => (
      <option key={d} value={d}>{d}</option>
    ))}
  </select>
</div>
                        {/* File */}
                        <div className="mb-3">
                            <label className="form-label">Upload Files</label>
                            <input type="file" className="form-control" multiple onChange={(e) => {
                                const selectedFiles = Array.from(e.target.files);

                                if (selectedFiles.length > MAX_FILES) {
                                    Swal.fire('Error', 'You can upload a maximum of 10 files.', 'error');
                                    e.target.value = null;
                                    return;
                                }
                                for (const file of selectedFiles) {
                                    if (file.size > MAX_FILE_SIZE) {
                                        Swal.fire('Error', 'Each file must be less than 10 MB.', 'error');
                                        e.target.value = null;
                                        return;
                                    }
                                }
                                handleChange('files', selectedFiles);
                            }}


                            />
                            {uploadResult?.files?.length > 0 && (
                                <small className="text-muted">
                                    {uploadResult.files.length} file(s) selected
                                </small>
                            )}
                        </div>


                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowUploadModal(false)}>Close</Button>
                        <Button variant="primary" type="submit" disabled={isLoading}>{isLoading ? "Saving..." : "Save"}</Button>
                    </Modal.Footer>
                </form>
            </Modal>


        </div>
    );
};

export default DrugScreeningResult;
